import { useEffect, useRef, FC } from 'react';
import { useDeviceInfo, useDispatch, useI18n, useSelector } from '~Hooks';
import * as actions from '~actions';

const PRINT_QUEUE_TOAST_ID = 'print-queue-banner';

/**
 * Shows status banner for what is printing on handheld devices
 * This is because staff usually close the device after punching in items, which in turn can lead to code executing stops.
 * So this is a way for them to know when they can safely turn the device off.
 */
const PrintQueueBanner: FC = () => {
  const printQueue = useSelector((state) => state.POSPrintQueue);
  const { i18n } = useI18n();
  const dispatch = useDispatch();
  const prevTotalRef = useRef(0);
  /** Denominator locked to the batch's starting total so the count climbs instead of shrinking. */
  const sessionTotalRef = useRef(0);
  const deviceInfo = useDeviceInfo();

  // Exclude errored prints from the active queue
  const activePrintQueue = printQueue.filter((p) => !p.error);
  const completed = activePrintQueue.filter((p) => p.success).length;
  const total = activePrintQueue.length;

  useEffect(() => {
    if (!deviceInfo?.isHandheldTerminal) {
      return;
    }

    // Queue just went from having items to empty — show completion toast briefly
    if (prevTotalRef.current > 0 && total === 0) {
      dispatch(actions.removeToast(PRINT_QUEUE_TOAST_ID));
      dispatch(actions.addToast(i18n('Printer.Banner.Done'), false, PRINT_QUEUE_TOAST_ID));
      setTimeout(() => {
        dispatch(actions.removeToast(PRINT_QUEUE_TOAST_ID));
      }, 2000);
      /** Reset so the next batch starts a fresh count. */
      sessionTotalRef.current = 0;
    }
    // Active prints in queue — show loading toast
    else if (total > 0) {
      /** Grow the locked total on batch start or mid-batch additions; never shrink it. */
      if (prevTotalRef.current === 0) {
        sessionTotalRef.current = total;
      } else if (total > prevTotalRef.current) {
        sessionTotalRef.current += total - prevTotalRef.current;
      }

      const sessionTotal = sessionTotalRef.current;
      /** Items still in queue but not yet successful. */
      const pending = total - completed;
      /** Everything finished, including prints already removed from the queue. */
      const done = Math.max(0, sessionTotal - pending);

      dispatch(actions.removeToast(PRINT_QUEUE_TOAST_ID));
      dispatch(
        actions.addToast(
          `${i18n('Printer.Banner.Printing')} ${done}/${sessionTotal}`,
          false,
          PRINT_QUEUE_TOAST_ID,
          false,
          undefined,
          true,
        ),
      );
    }

    prevTotalRef.current = total;
  }, [total, completed, deviceInfo?.isHandheldTerminal]);

  // Clean up toast on unmount
  useEffect(() => {
    return () => {
      dispatch(actions.removeToast(PRINT_QUEUE_TOAST_ID));
    };
  }, []);

  return null;
};

export default PrintQueueBanner;
